// Shared QR metadata for PNG exports.
//
// The exported PNG has a 4:3 (height:width) layout: a metadata strip at the
// top with a QR code (left, encoding version + time range) and human-readable
// dates (right), followed by the square heatmap plot area below.
//
// The QR payload is a comma-delimited string:
// `{metadata_version},{t0},{t1},{version},{count}`.
// The metadata version lets the decoder reject payloads it doesn't understand.

/** Width of the exported PNG in pixels. The plot area is square: PLOT_SIZE × PLOT_SIZE. */
export const PNG_WIDTH = 1080;
/** Height of the square plot area (the heatmap itself). */
export const PLOT_SIZE = 1080;
/** Total height of the exported PNG (4:3 height:width ratio). */
export const PNG_HEIGHT = Math.round((PNG_WIDTH * 4) / 3); // 1440
/** Height of the metadata strip at the top. */
export const STRIP_HEIGHT = PNG_HEIGHT - PLOT_SIZE; // 360
/** Size of the QR code in the metadata strip (square). */
export const QR_SIZE = 300;
/** Left padding for the QR code within the strip. */
export const QR_PADDING = 30;

/** Current QR metadata format version. */
export const METADATA_VERSION = 1;

export interface PngMetadata {
    /** Metadata format version (currently 1). */
    metadataVersion: number;
    /** Start timestamp (ms since epoch). */
    t0: number;
    /** End timestamp (ms since epoch). */
    t1: number;
    /** Projection version of the data points. */
    version: number;
    /** Number of data points in the time range + version. */
    count: number;
}

/**
 * Encode metadata into the QR payload string.
 * Format: `{metadata_version},{t0},{t1},{version},{count}`
 */
export function encodeMetadata(meta: PngMetadata): string {
    return `${meta.metadataVersion},${meta.t0},${meta.t1},${meta.version},${meta.count}`;
}

/**
 * Decode a QR payload string into metadata.
 * Returns null if the string is not a valid metadata payload.
 */
export function decodeMetadata(data: string): PngMetadata | null {
    const parts = data.split(",");
    if (parts.length !== 5) return null;
    const metadataVersion = Number(parts[0]);
    const t0 = Number(parts[1]);
    const t1 = Number(parts[2]);
    const version = Number(parts[3]);
    const count = Number(parts[4]);
    if (
        !Number.isFinite(metadataVersion) ||
        !Number.isInteger(metadataVersion) ||
        metadataVersion !== METADATA_VERSION ||
        !Number.isFinite(t0) ||
        !Number.isFinite(t1) ||
        !Number.isFinite(version) ||
        !Number.isInteger(version) ||
        !Number.isFinite(count) ||
        !Number.isInteger(count)
    ) {
        return null;
    }
    return { metadataVersion, t0, t1, version, count };
}

/**
 * Format a timestamp (ms since epoch) as a human-readable date string.
 * Returns "all time" for t0=0.
 */
export function formatTimestamp(ms: number): string {
    if (ms === 0) return "all time";
    return new Date(ms).toISOString().slice(0, 10); // YYYY-MM-DD
}
